/**
 * UI adapter: bridges React flows to IProgressRepository (platform session/checkpoint sync)
 * Enable via architectureFeatures.usePlatformPersistence
 */
import { useCallback } from 'react';
import type {
  GameSessionRef,
  ProgressEventInput,
} from '../../../domain/ports/IProgressRepository';
import { progressRepository } from '../../../infrastructure/di/container';
import { architectureFeatures } from '../../../infrastructure/config/features';
import {
  PLAYER_REF_STORAGE_KEY,
  SESSION_STORAGE_KEY,
} from '../../../infrastructure/config/platform';

export function getStoredPlatformSessionId(): string | null {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  return sessionStorage.getItem(SESSION_STORAGE_KEY);
}

const storeSessionId = (sessionId: string): void => {
  if (typeof sessionStorage !== 'undefined' && sessionId) {
    sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  }
};

/** Stable anonymous learner id for progress analytics */
export function getOrCreatePlayerRef(): string {
  if (typeof localStorage === 'undefined') {
    return `player_${Date.now()}`;
  }
  const existing = localStorage.getItem(PLAYER_REF_STORAGE_KEY);
  if (existing) {
    return existing;
  }
  const ref =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `player_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  localStorage.setItem(PLAYER_REF_STORAGE_KEY, ref);
  return ref;
}

/** Create a server-side training session; returns null when flag is off */
export function createPlatformSession(
  industry?: string,
  playerRef?: string,
): Promise<GameSessionRef | null> {
  if (!architectureFeatures.usePlatformPersistence) {
    return Promise.resolve(null);
  }
  const ref = playerRef ?? getOrCreatePlayerRef();
  return progressRepository.createSession(industry, ref).then((sessionRef) => {
    storeSessionId(sessionRef.sessionId);
    return sessionRef;
  });
}

/** Ensure a platform session exists (e.g. after resume from local-only save) */
export async function ensurePlatformSession(
  industry?: string,
): Promise<GameSessionRef | null> {
  if (!architectureFeatures.usePlatformPersistence) {
    return null;
  }
  const existing = getStoredPlatformSessionId();
  if (existing) {
    return { sessionId: existing, tenantId: '' };
  }
  return createPlatformSession(industry, getOrCreatePlayerRef());
}

/** Append a progress/analytics event using the stored session id */
export function recordPlatformEventAuto(event: ProgressEventInput): void {
  const sessionId = getStoredPlatformSessionId();
  if (!sessionId) {
    return;
  }
  recordPlatformEvent(sessionId, event);
}

/** Append a progress/analytics event to the platform API (fire-and-forget when flag is on) */
export function recordPlatformEvent(sessionId: string, event: ProgressEventInput): void {
  if (!architectureFeatures.usePlatformPersistence || !sessionId) {
    return;
  }
  void progressRepository.recordEvent(sessionId, event).catch((err: unknown) => {
    console.warn(
      '[platform] event recording failed',
      err instanceof Error ? err.message : err,
    );
  });
}

/**
 * Hook helper for App.tsx integration.
 * Returns stable callbacks that talk to the platform API when the feature flag is on.
 */
export function usePlatformPersistence() {
  const createSession = useCallback(
    (industry?: string, playerRef?: string) =>
      createPlatformSession(industry, playerRef),
    [],
  );

  const ensureSession = useCallback(
    (industry?: string) => ensurePlatformSession(industry),
    [],
  );

  const recordEvent = useCallback(
    (event: ProgressEventInput) => recordPlatformEventAuto(event),
    [],
  );

  return {
    createPlatformSession: createSession,
    ensurePlatformSession: ensureSession,
    recordPlatformEvent: recordEvent,
  };
}